import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { VavClient, Message } from '@vav/client-core';
import type { AttachmentRef } from '@vav/protocol';
import { AudioPlayer, VideoPlayer, VoicePlayer } from './MediaPlayers.tsx';
import { senderColourIndex } from './Avatar.tsx';
import { formatTime } from '../format.ts';
import { useT } from '../i18n.tsx';
import { useBackButton } from '../useBackButton.ts';
import { linkify } from '../linkify.tsx';

interface Props {
  client: VavClient;
  message: Message;
  senderName?: string;
  showSender: boolean;
  onReply: (message: Message) => void;
  onReact: (message: Message, emoji: string) => void;
  onDelete: (message: Message) => void;
  onDeleteForMe: (message: Message) => void;
  onForward: (message: Message) => void;
  onCancelScheduled?: (message: Message) => void;
  onEdit?: (message: Message) => void;
  /** The message this one answers, when it is still in the loaded history. */
  repliedTo?: Message;
  /** Display name of whoever wrote it. */
  repliedToName?: string;
  /** Jump to the quoted message. */
  onJumpTo?: (messageId: string) => void;
  /** Enter multi-select with this message already picked. */
  onSelect: (message: Message) => void;
  /** Non-null while the thread is in selection mode. */
  selected?: boolean;
  onToggleSelected?: (message: Message) => void;
}

/** The row that is always visible. One tap, no menu. */
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '🙏'];

/**
 * The rest, behind "more".
 *
 * A grid rather than a full emoji keyboard: reactions are a small vocabulary
 * people reuse, and every extra row costs the scan time that made the quick row
 * worth having. Grouped roughly by what they are *for* -- agreeing, laughing,
 * feeling, marking -- because that is how someone looks for one.
 */
const MORE_REACTIONS = [
  '😍', '🥰', '😘', '🤗', '🤩', '😎',
  '😢', '😭', '😡', '🤯', '😱', '🥺',
  '🎉', '🔥', '💯', '👏', '🙌', '🤝',
  '👌', '✌️', '🤞', '💪', '🫡', '🙄',
  '✅', '❌', '❓', '❗', '⭐', '💔',
];

export function MessageBubble({
  client,
  message,
  senderName,
  showSender,
  onReply,
  onReact,
  onDelete,
  onDeleteForMe,
  onForward,
  onCancelScheduled,
  onEdit,
  repliedTo,
  repliedToName,
  onJumpTo,
  onSelect,
  selected,
  onToggleSelected,
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showAllReactions, setShowAllReactions] = useState(false);
  const moreRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  /** Viewport coordinates, once measured. Undefined means "not placed yet". */
  const [menuAt, setMenuAt] = useState<{ top: number; left: number }>();
  const outgoing = message.outgoing;
  const t = useT();
  const selecting = selected !== undefined;

  function closeMenu() {
    setMenuOpen(false);
    setShowAllReactions(false);
    setMenuAt(undefined);
  }

  /**
   * Put the menu on screen, wherever the message happens to be.
   *
   * CSS cannot do this. Anchored inside the bubble, the menu inherited the
   * bubble's position: a message low in the thread opened one below the fold,
   * and a message at the right edge opened one past the right edge — off screen
   * entirely, with nothing to scroll to.
   *
   * So it is measured and placed against the viewport instead: it opens from
   * the button towards the middle of the screen, flips above when there is no
   * room below, and is clamped into the viewport as a last resort. Re-run when
   * the extra reactions expand, because that changes its height.
   */
  useLayoutEffect(() => {
    if (!menuOpen) return;

    const place = () => {
      const button = moreRef.current?.getBoundingClientRect();
      const menu = menuRef.current?.getBoundingClientRect();
      if (!button || !menu) return;

      const margin = 8;
      // Open away from the edge the button sits nearest, so the menu grows
      // towards the middle of the screen rather than off the side.
      const towardsStart = button.left > window.innerWidth / 2;
      let left = towardsStart ? button.right - menu.width : button.left;
      left = Math.min(Math.max(margin, left), window.innerWidth - menu.width - margin);

      let top = button.bottom + 4;
      if (top + menu.height > window.innerHeight - margin) {
        const above = button.top - menu.height - 4;
        // Above if it fits there; otherwise pin it and let the menu scroll.
        top = above >= margin ? above : Math.max(margin, window.innerHeight - menu.height - margin);
      }

      setMenuAt({ top, left });
    };

    place();
    window.addEventListener('resize', place);
    // Capture, so scrolling the message list moves it too rather than leaving
    // it floating over an unrelated message.
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [menuOpen, showAllReactions]);

  if (message.deleted) {
    return (
      <div className={`row ${outgoing ? 'row--out' : 'row--in'}`}>
        <div className="bubble bubble--deleted">
          <em className="muted">{t('bubble.deleted')}</em>
        </div>
      </div>
    );
  }

  const content = message.content;

  return (
    <div
      data-message={message.id}
      className={`row ${outgoing ? 'row--out' : 'row--in'} ${selecting ? 'row--selecting' : ''} ${selected ? 'is-selected' : ''}`}
      /* In selection mode the whole row is the target: aiming at a small
         checkbox while picking several messages is the wrong gesture. */
      onClick={selecting ? () => onToggleSelected?.(message) : undefined}
    >
      {selecting && (
        <span className={`row__check ${selected ? 'is-on' : ''}`} aria-hidden="true">
          {selected ? '✓' : ''}
        </span>
      )}
      <div
        className="bubble"
        onDoubleClick={() => !selecting && onReply(message)}
      >
        {showSender && !outgoing && senderName && (
          <div
            className="bubble__sender"
            dir="auto"
            /* Same index as the avatar, so a sender's name and face agree. */
            style={{ color: `var(--sender-${senderColourIndex(senderName)})` }}
          >
            {senderName}
          </div>
        )}

        {/* What this is answering.

            Without it a reply is a sentence with its subject missing: "yes, at
            eight" arrives under whatever happens to be above it, which in a
            busy thread is rarely the question. The quote travels with the
            message, so it reads the same for the person who sent it and the
            person who receives it. */}
        {message.replyTo && (
          <button
            className="quote"
            onClick={() => message.replyTo && onJumpTo?.(message.replyTo)}
            disabled={!repliedTo}
          >
            {repliedTo ? (
              <>
                <span className="quote__who" dir="auto">
                  {repliedTo.outgoing ? t('quote.you') : (repliedToName ?? t('quote.them'))}
                </span>
                <span className="quote__text" dir="auto">
                  {previewOf(repliedTo, t)}
                </span>
              </>
            ) : (
              // Deleted, or older than the loaded window. Saying so beats
              // rendering a reply that appears to answer nothing.
              <span className="quote__text muted">{t('quote.unavailable')}</span>
            )}
          </button>
        )}

        {content.kind === 'media' && <Attachment client={client} attachment={content.attachment} />}

        {'text' in content && content.text && (
          <p className="bubble__text" dir="auto">
            {linkify(content.text)}
          </p>
        )}

        <div className="bubble__meta">
          {message.edited && <span className="bubble__edited">{t('bubble.edited')}</span>}
          <time>{formatTime(message.ts)}</time>
          {outgoing && <Ticks state={message.state} />}
        </div>

        {message.reactions && Object.keys(message.reactions).length > 0 && (
          <div className="reactions">
            {Object.entries(message.reactions).map(([emoji, users]) => (
              <span key={emoji} className="reaction">
                {emoji} {users.length > 1 ? users.length : ''}
              </span>
            ))}
          </div>
        )}



      </div>

      {/* Outside the bubble, never over the words.

          It used to be absolutely positioned in the bubble's top corner, where
          it sat on top of the first line of text and, on a short message,
          covered it outright. As a sibling it simply takes its own space beside
          the bubble, and `order` keeps it on the inner side so it is never
          pushed against the edge of the screen. */}
      {!selecting && (
        <button
          ref={moreRef}
          className="bubble__more"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((v) => !v);
          }}
          aria-label={t('bubble.actions')}
          aria-expanded={menuOpen}
        >
          ⋯
        </button>
      )}

      {menuOpen && (
        <>
          {/* A backdrop, so a tap anywhere dismisses the menu. On a phone
              there is no mouseleave to close it with. */}
          <div className="bubbleMenu__scrim" onClick={closeMenu} />
          <div
            ref={menuRef}
            className="bubbleMenu"
            style={
              menuAt
                ? { top: menuAt.top, left: menuAt.left }
                : // Rendered but not yet measured. Hidden rather than absent,
                  // because it must be in the document to have a size at all,
                  // and showing it at 0,0 for a frame is a visible jump.
                  { visibility: 'hidden' }
            }
          >
            <div className="bubbleMenu__reactions">
              {QUICK_REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => {
                    onReact(message, emoji);
                    closeMenu();
                  }}
                >
                  {emoji}
                </button>
              ))}
              <button
                className="bubbleMenu__moreEmoji"
                onClick={() => setShowAllReactions((v) => !v)}
                aria-label={t('bubble.moreReactions')}
                aria-expanded={showAllReactions}
              >
                {showAllReactions ? '▴' : '＋'}
              </button>
            </div>

            {showAllReactions && (
              <div className="bubbleMenu__reactionGrid">
                {MORE_REACTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => {
                      onReact(message, emoji);
                      closeMenu();
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}

            <button
              onClick={() => {
                onReply(message);
                closeMenu();
              }}
            >
              ↩︎ {t('bubble.reply')}
            </button>
            <button
              onClick={() => {
                onForward(message);
                closeMenu();
              }}
            >
              ➦ {t('bubble.forward')}
            </button>
            <button
              onClick={() => {
                onSelect(message);
                closeMenu();
              }}
            >
              ☑︎ {t('bubble.select')}
            </button>
            {/* Available on every message, including ones received: wanting a
                photo off your own phone has nothing to do with who sent it. */}
            <button
              onClick={() => {
                onDeleteForMe(message);
                closeMenu();
              }}
            >
              🗑️ {t('bubble.deleteForMe')}
            </button>
            {outgoing && (
              <button
                className="danger"
                onClick={() => {
                  onDelete(message);
                  closeMenu();
                }}
              >
                {t('bubble.deleteForEveryone')}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Delivery state.
 *
 * Every state differs in *shape*, not only colour: queued is a clock, sent is
 * one light check, delivered is two light checks, read is two heavy checks, and
 * failed is a warning sign. Read is additionally brass, because brass carries
 * information here -- but a user who cannot distinguish brass from the muted
 * tone still sees a different glyph, which is the requirement. Colour alone is
 * never the signal.
 *
 * U+FE0E on the heavy check and the warning sign forces text presentation;
 * without it some platforms substitute a colour emoji and the shape distinction
 * turns back into a colour one.
 */
function Ticks({ state }: { state: Message['state'] }) {
  const label = useT()(`bubble.state.${state}` as const);

  const glyph = {
    pending: '🕐',
    sent: '✓',
    delivered: '✓✓',
    read: '✔︎✔︎',
    failed: '⚠︎',
    /* Waiting by choice, so a calendar rather than the pending clock: the two
       states look identical from outside and mean opposite things. */
    scheduled: '🗓',
  }[state];

  const modifier =
    state === 'read' ? ' ticks--read' : state === 'failed' ? ' ticks--failed' : '';

  // role/aria-label rather than title: a title attribute is not reliably
  // announced, and delivery state is exactly what a screen reader user cannot
  // otherwise infer.
  return (
    <span className={`ticks${modifier}`} role="img" aria-label={label}>
      {glyph}
    </span>
  );
}

/**
 * Attachments download and decrypt on demand, then render from a blob URL.
 * Nothing is fetched until the bubble is on screen and the user is looking at
 * it, which keeps a scrollback of images from costing a burst of requests.
 */
function Attachment({ client, attachment }: { client: VavClient; attachment: AttachmentRef }) {
  const [url, setUrl] = useState<string>();
  const [failed, setFailed] = useState(false);
  const [viewing, setViewing] = useState(false);
  const t = useT();
  // Stable identity: the viewer registers a back layer keyed on this callback,
  // and a new function each render would push a history entry each render.
  const closeViewer = useCallback(() => setViewing(false), []);

  const isImage = attachment.mime.startsWith('image/');
  const isVideo = attachment.mime.startsWith('video/');
  const isAudio = attachment.mime.startsWith('audio/');
  // Anything playable gets decrypted up front; a document waits for a click.
  const playable = isImage || isVideo || isAudio;

  useEffect(() => {
    if (!playable) return;
    let revoked = false;
    let objectUrl: string | undefined;

    void (async () => {
      try {
        const bytes = await client.downloadAttachment(attachment);
        if (revoked) return;
        objectUrl = URL.createObjectURL(new Blob([bytes.slice().buffer as ArrayBuffer], { type: attachment.mime }));
        setUrl(objectUrl);
      } catch {
        setFailed(true);
      }
    })();

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [client, attachment, playable]);

  async function download() {
    const bytes = await client.downloadAttachment(attachment);
    const objectUrl = URL.createObjectURL(
      new Blob([bytes.slice().buffer as ArrayBuffer], { type: attachment.mime }),
    );
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = attachment.name;
    // The link has to be in the document for the click to count in Firefox,
    // and the URL has to outlive the click: revoking it on the next line
    // cancelled the download before the browser had begun reading it, which is
    // why saving a file quietly did nothing.
    link.style.display = 'none';
    document.body.append(link);
    link.click();
    setTimeout(() => {
      link.remove();
      URL.revokeObjectURL(objectUrl);
    }, 60_000);
  }

  if (failed) return <p className="error">{t('bubble.decryptFailed')}</p>;

  // A recorded voice note reads as a message; an attached audio file reads as
  // a document with a player. Same mime type, different thing entirely.
  if (attachment.voice) return <VoicePlayer url={url} attachment={attachment} />;
  if (isVideo) return <VideoPlayer url={url} attachment={attachment} />;
  if (isAudio) return <AudioPlayer url={url} attachment={attachment} />;

  if (!isImage) {
    return (
      <button className="fileCard" onClick={() => void download()}>
        <span className="fileCard__icon">{iconFor(attachment.mime, attachment.name)}</span>
        <span>
          <strong>{attachment.name}</strong>
          <small className="muted">{formatBytes(attachment.size)}</small>
        </span>
      </button>
    );
  }

  return (
    <>
      <div
        className="imageWrap"
        style={{ aspectRatio: attachment.width && attachment.height ? `${attachment.width}/${attachment.height}` : undefined }}
      >
        {url ? (
          // Tapping a photo opens it. It used to start a download instead,
          // which is neither what the gesture means nor what people do with a
          // picture someone just sent them.
          <img
            src={url}
            alt={attachment.name}
            loading="lazy"
            onClick={() => setViewing(true)}
          />
        ) : (
          <div className="imageWrap__loading">{t('bubble.decrypting')}</div>
        )}
      </div>

      {viewing && url && (
        <Lightbox url={url} name={attachment.name} onSave={() => void download()} onClose={closeViewer} />
      )}
    </>
  );
}

/**
 * Full-screen image viewer.
 *
 * It registers a back layer, so the device Back gesture closes the photo and
 * leaves the conversation open. Without one, Back fell through to the thread's
 * own layer and threw the user out of the conversation they were looking at —
 * having to reopen the chat to get back to where a photo had been tapped.
 */
function Lightbox({
  url,
  name,
  onSave,
  onClose,
}: {
  url: string;
  name: string;
  onSave: () => void;
  onClose: () => void;
}) {
  const t = useT();

  // Back closes the photo, not the conversation.
  useBackButton(true, onClose);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    // Scrolling the thread behind a full-screen image does nothing useful and
    // on a phone it drags the picture around with it.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return (
    <div className="lightbox" role="dialog" aria-modal="true" aria-label={name} onClick={onClose}>
      <div className="lightbox__bar" onClick={(e) => e.stopPropagation()}>
        <span className="lightbox__name">
          {name}
        </span>
        <button className="icon" onClick={onSave} aria-label={t('bubble.save')}>
          ⤓
        </button>
        <button className="icon" onClick={onClose} aria-label={t('bubble.closeImage')}>
          ✕
        </button>
      </div>
      {/* The click that closes sits on the backdrop, so tapping the picture
          itself must not bubble up to it. */}
      <img src={url} alt={name} onClick={(e) => e.stopPropagation()} />
    </div>
  );
}

/**
 * One line standing in for a whole message.
 *
 * A caption when there is one, because that is what the sender actually wrote;
 * otherwise the kind of thing it was. An attachment with no caption has no text
 * to quote, and "photo" is more use than an empty line.
 */
export function previewOf(message: Message, t: ReturnType<typeof useT>): string {
  if (message.deleted) return t('bubble.deleted');
  const content = message.content;
  if ('text' in content && content.text) return content.text;
  if (content.kind === 'media') {
    const mime = content.attachment.mime;
    if (content.attachment.voice) return t('quote.voice');
    if (mime.startsWith('image/')) return t('quote.image');
    if (mime.startsWith('video/')) return t('quote.video');
    if (mime.startsWith('audio/')) return t('quote.audio');
    return content.attachment.name;
  }
  return t('thread.attachment');
}

/** A recognisable glyph beats a paperclip for spotting a file in scrollback. */
function iconFor(mime: string, name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (mime === 'application/pdf' || ext === 'pdf') return '📕';
  if (/word|document/.test(mime) || ['doc', 'docx', 'odt'].includes(ext)) return '📘';
  if (/sheet|excel/.test(mime) || ['xls', 'xlsx', 'csv', 'ods'].includes(ext)) return '📗';
  if (/presentation|powerpoint/.test(mime) || ['ppt', 'pptx'].includes(ext)) return '📙';
  if (/zip|compressed|tar|rar|7z/.test(mime) || ['zip', 'rar', '7z', 'gz'].includes(ext)) return '🗜️';
  if (mime.startsWith('text/')) return '📄';
  return '📎';
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
